import { t } from '@/i18n/i18n'
import router from '@/router'
import { ErrorService } from '@/shared/service/errorService'
import {
  FilesService,
  type ImageTransformations,
} from '@/shared/service/files.service'
import { IdsService } from '@/shared/service/utils.service'
import { SnackbarService } from '@/shared/snackbar/snackbar.service'
import { create } from '@/shared/utils/zustandVue'
import { cloneDeep } from '@/utils'

import { type Lang } from '../../../types/TranslatableText.type'
import { useAppConfigStore } from '../../shared/store/app-config.store'
import {
  type Button,
  type EditorButtonTarget,
  type Info,
  type Section,
} from './Infos'
import { type InfosPratiqueUsage, InfosService } from './infos.service'

export type StateButton = Omit<Button, 'target'> & {
  target: EditorButtonTarget
}

export type StateInfo = Omit<Info, 'sections'> & {
  sections: Record<string, StateSection>
}
export type StateSection = Omit<Section, 'buttons'> & {
  buttons: Record<string, StateButton>
}

// a new info has no _id nor icon yet
type EditingInfo = Omit<StateInfo, '_id' | 'icon'> &
  Partial<Pick<StateInfo, '_id' | 'icon'>>

function omitKey<T extends Record<string, unknown>>(record: T, key: string): T {
  const { [key]: _removed, ...rest } = record
  return rest as T
}

class InfosState {
  infos: Record<string, StateInfo> = {}
  deleteInfoId: string | null = null
  loading = false

  //form
  editingInfo: Partial<StateInfo> | null = null
  infoToMoveId: string | null = null

  //sections
  sections: Record<string, StateSection> = {}
  deletingSectionId: string | null = null
  //preview
  selectedInfo: string | null = null

  togglingVisibilityInfoId: string | null = null
  infoPratiqueUsage: InfosPratiqueUsage | null = null
  showVisibilityModal = false
}

type InfosStore = InfosState & {
  reset: () => void
  unload: () => void
  load: () => void
  setInfos: (infos: StateInfo[]) => void
  hideInfoMutation: (id: string) => void
  setDeleting: (id: string) => void
  setSelectedInfo: (info: string) => void
  setEditing: (info: EditingInfo) => void
  addSection: () => void
  setDeletingSection: (id: string) => void
  cancelSectionDeleting: () => void
  deleteSection: () => void
  setSectionName: (payload: { id: string; value: string; lang: Lang }) => void
  setSectionExpand: (payload: { id: string; value: boolean }) => void
  setSectionDescription: (payload: {
    id: string
    value: string
    lang: Lang
  }) => void
  moveSection: (payload: { sectionId: string; up_down: number }) => void
  addButton: (sectionId: string) => void
  removeButton: (payload: { sectionId: string; buttonId: string }) => void
  setButtonName: (payload: {
    sectionId: string
    buttonId: string
    value: string
    lang: Lang
  }) => void
  setButtonTarget: (payload: {
    sectionId: string
    buttonId: string
    value: EditorButtonTarget
  }) => void
  setButtonIcon: (payload: {
    sectionId: string
    id: string
    value: string
  }) => void
  closeVisibilityModal: () => void
  moveInfo: (payload: { infoId: string; up_down: number }) => void
  list: () => Promise<void>
  initHide: (infoId: string) => Promise<void>
  hideInfo: (id: string) => Promise<void>
  deleteInfo: () => Promise<void>
  addInfo: (payload: {
    info: StateInfo
    imageTransformations: ImageTransformations | null | undefined
  }) => Promise<void>
  loadInfo: (infoId: string | undefined) => Promise<void>
  moveInfoAction: (payload: {
    infoToMove: StateInfo
    up_down: number
  }) => Promise<void>
}

// --- Getter compute functions (pure, operate on state) ---

export function computeGetInfos(state: InfosState): StateInfo[] {
  const tab = cloneDeep(state.infos)
  return Object.values(tab)
}

export function computeGetSortedSections(state: InfosState): StateSection[] {
  const sortedSections = Object.values(state.sections)
  return sortedSections.sort(function (a, b) {
    return b.weight - a.weight
  })
}

export function computeGetSections(state: InfosState): StateSection[] {
  return Object.values(state.sections)
}

export const useInfosStore = create<InfosStore>((set, get) => ({
  ...new InfosState(),

  reset: () => {
    set({
      sections: {},
      deleteInfoId: null,
      editingInfo: null,
      loading: false,
    })
  },

  unload: () => {
    set({ loading: false })
  },

  load: () => {
    set({ loading: true })
  },

  setInfos: (infos) => {
    set({
      infos: infos.reduce((acc: Record<string, StateInfo>, info: StateInfo) => {
        acc[info._id] = info
        return acc
      }, {}),
    })
  },

  hideInfoMutation: (id) => {
    const infos = { ...get().infos }
    infos[id] = {
      ...infos[id],
      showInfoPratique: !infos[id].showInfoPratique,
    }
    set({ infos })
  },

  setDeleting: (id) => {
    set({ deleteInfoId: id })
  },

  setSelectedInfo: (info) => {
    set({ selectedInfo: info })
  },

  setEditing: (info) => {
    const sections = { ...get().sections }
    Object.values(info.sections).forEach((section) => {
      let sectionId: string
      do {
        sectionId = IdsService.generateId()
      } while (sectionId in sections)
      sections[sectionId] = { ...section, buttons: {} }
      Object.values(section.buttons).forEach((button) => {
        let buttonId
        do {
          buttonId = IdsService.generateId()
        } while (buttonId in sections[sectionId].buttons)
        sections[sectionId].buttons[buttonId] = button
      })
    })
    set({ editingInfo: { ...info }, sections: { ...sections } })
  },

  addSection: () => {
    const sections = { ...get().sections }
    Object.values(sections).forEach((section) => {
      section.weight++
    })
    let id
    do {
      id = IdsService.generateId()
    } while (id in sections)
    sections[id] = {
      names: {
        fr: '',
        en: '',
      },
      weight: 0,
      buttons: {},
      links: null,
      descriptions: {
        fr: '',
        en: '',
      },
      expand: true,
    }
    set({ sections: { ...sections } })
  },

  setDeletingSection: (id) => {
    set({ deletingSectionId: id })
  },

  cancelSectionDeleting: () => {
    set({ deletingSectionId: null })
  },

  deleteSection: () => {
    const state = get()
    const { deletingSectionId } = state
    if (deletingSectionId != null) {
      const { weight } = state.sections[deletingSectionId]
      const sections = omitKey(state.sections, deletingSectionId)
      Object.values(sections).forEach((section) => {
        if (section.weight > weight) section.weight--
      })
      set({
        sections: { ...sections },
        deletingSectionId: null,
      })
    }
  },

  setSectionName: (payload) => {
    const sections = { ...get().sections }
    sections[payload.id] = {
      ...sections[payload.id],
      names: {
        ...sections[payload.id].names,
        [payload.lang]: payload.value,
      },
    }
    set({ sections })
  },

  setSectionExpand: (payload) => {
    const sections = { ...get().sections }
    sections[payload.id] = { ...sections[payload.id], expand: payload.value }
    set({ sections })
  },

  setSectionDescription: (payload) => {
    const sections = { ...get().sections }
    sections[payload.id] = {
      ...sections[payload.id],
      descriptions: {
        ...sections[payload.id].descriptions,
        [payload.lang]: payload.value,
      },
    }
    set({ sections })
  },

  moveSection: (payload) => {
    const sections = { ...get().sections }
    const tab = Object.values(sections).sort((a, b) => {
      return a.weight - b.weight
    })
    const index = tab.indexOf(sections[payload.sectionId])
    const { weight } = tab[index]
    tab[index].weight = tab[index + payload.up_down].weight
    tab[index + payload.up_down].weight = weight
    set({ sections: { ...sections } })
  },

  addButton: (sectionId) => {
    const sections = { ...get().sections }
    let id
    do {
      id = IdsService.generateId()
    } while (id in sections[sectionId].buttons)

    sections[sectionId] = {
      ...sections[sectionId],
      buttons: {
        ...sections[sectionId].buttons,
        [id]: {
          names: {
            fr: '',
            en: '',
          },
          icon: '',
          target: { type: 'EXTERNAL_URL', url: '' },
        },
      },
    }
    set({ sections })
  },

  removeButton: (payload) => {
    const sections = { ...get().sections }
    const section = { ...sections[payload.sectionId] }
    const buttons = omitKey(section.buttons, payload.buttonId)
    section.buttons = buttons
    sections[payload.sectionId] = section
    set({ sections })
  },

  setButtonName: (payload) => {
    const sections = { ...get().sections }
    const section = { ...sections[payload.sectionId] }
    const buttons = { ...section.buttons }
    const button = { ...buttons[payload.buttonId] }
    button.names = { ...button.names, [payload.lang]: payload.value }
    buttons[payload.buttonId] = button
    section.buttons = buttons
    sections[payload.sectionId] = section
    set({ sections })
  },

  setButtonTarget: (payload) => {
    const sections = { ...get().sections }
    const section = { ...sections[payload.sectionId] }
    const buttons = { ...section.buttons }
    const button = { ...buttons[payload.buttonId], target: payload.value }
    buttons[payload.buttonId] = button
    section.buttons = buttons
    sections[payload.sectionId] = section
    set({ sections })
  },

  setButtonIcon: (payload) => {
    const sections = { ...get().sections }
    const section = { ...sections[payload.sectionId] }
    const buttons = { ...section.buttons }
    const button = { ...buttons[payload.id], icon: payload.value }
    buttons[payload.id] = button
    section.buttons = buttons
    sections[payload.sectionId] = section
    set({ sections })
  },

  closeVisibilityModal: () => {
    set({
      showVisibilityModal: false,
      togglingVisibilityInfoId: null,
      infoPratiqueUsage: {
        pageContents: [],
        navBarElements: [],
      },
    })
  },

  moveInfo: (payload) => {
    const infos = { ...get().infos }
    const tab = Object.values(infos).sort((a, b) => {
      if (a.weight === b.weight) {
        if (a.names.fr < b.names.fr) return -1
        if (a.names.fr > b.names.fr) return 1
        return 0
      }
      return a.weight - b.weight
    })
    const index = tab.indexOf(infos[payload.infoId])
    const { weight } = tab[index]
    tab[index].weight = tab[index + payload.up_down].weight
    tab[index + payload.up_down].weight = weight
    set({
      infoToMoveId: tab[index + payload.up_down]._id,
      infos: { ...infos },
    })
  },

  list: async () => {
    get().load()
    try {
      await useAppConfigStore.getState().loadAppConfig()
      const infos = await InfosService.getAll()
      get().setInfos(infos)
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  initHide: async (infoId) => {
    const state = get()
    // fetch usage only when hidding the event
    const usage = state.infos[infoId].showInfoPratique
      ? await InfosService.checkUsage([infoId])
      : {
          pageContents: [],
          navBarElements: [],
        }
    // if used somewhere, show confirmation dialog
    if (usage.pageContents.length > 0 || usage.navBarElements.length > 0) {
      set({
        togglingVisibilityInfoId: infoId,
        infoPratiqueUsage: usage,
        showVisibilityModal: true,
      })
    } else {
      // else hide directly
      await get().hideInfo(infoId)
    }
  },

  hideInfo: async (id) => {
    get().load()
    try {
      const info = { ...get().infos[id] }
      info.showInfoPratique = !info.showInfoPratique
      await InfosService.updateInfo(info)
      get().hideInfoMutation(id)
      if (info.showInfoPratique) {
        SnackbarService.info(
          t('INFOS.SNACK_BAR.SHOW', {
            string: info.names.fr,
          }),
        )
      } else {
        SnackbarService.info(
          t('INFOS.SNACK_BAR.HIDE', {
            string: info.names.fr,
          }),
        )
      }
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
      get().closeVisibilityModal()
    }
  },

  deleteInfo: async () => {
    get().load()
    const state = get()
    if (state.deleteInfoId != null) {
      try {
        await InfosService.delete(state.deleteInfoId)
        SnackbarService.info(
          t('INFOS.SNACK_BAR.DELETE', {
            string: state.infos[state.deleteInfoId].names.fr,
          }),
        )
        await get().list()
      } catch (e) {
        ErrorService.handleError(e)
      } finally {
        get().reset()
      }
    }
  },

  addInfo: async ({ info, imageTransformations }) => {
    get().load()
    {
      try {
        const infoToSend = cloneDeep(info)
        // a new info has no _id (Vue 2 parity: keeps the default weight of 100)
        if (!infoToSend._id) {
          await InfosService.addInfo(infoToSend)
          SnackbarService.info(
            t('INFOS.SNACK_BAR.ADD', {
              string: info.names.fr,
            }),
          )
        } else {
          await InfosService.updateInfo(infoToSend)
          SnackbarService.info(
            t('INFOS.SNACK_BAR.EDIT', {
              string: info.names.fr,
            }),
          )
        }
        if (infoToSend.image != null && imageTransformations) {
          await FilesService.setImageTransform(
            infoToSend.image,
            imageTransformations,
          )
        }
        await router.push('/infosPratiques')
      } catch (e) {
        ErrorService.handleError(e)
      }
    }
  },

  loadInfo: async (infoId) => {
    get().reset()
    get().load()
    try {
      if (infoId != null) {
        get().setEditing(await InfosService.getInfoById(infoId))
      } else {
        get().setEditing({
          names: {
            fr: '',
            en: '',
          },
          sections: {},
          showInfoPratique: false,
          weight: 100,
          image: undefined,
          copyright: undefined,
        })
      }
    } catch (e) {
      ErrorService.handleError(e)
    }
  },

  moveInfoAction: async (payload) => {
    get().load()
    try {
      get().moveInfo({
        infoId: payload.infoToMove._id,
        up_down: payload.up_down,
      })
      const state = get()
      await InfosService.updateInfo(state.infos[payload.infoToMove._id])
      if (state.infoToMoveId != null) {
        await InfosService.updateInfo(state.infos[state.infoToMoveId])
      }
      await get().list()
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().reset()
    }
  },
}))
